import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { Locale, locales } from './locales';

// English is the source language: every other file is translated from en.json,
// and a missing key falls back to it rather than showing the key itself.
export const defaultLanguage = 'en';

export const supportedLanguages = locales.map(locale => locale.code);

export const languageLabel = (code: string): string =>
        locales.find(locale => locale.code === code)?.label ?? code;

// Both the code itself and anything a browser might report for it.
const codeFor = new Map<string, string>(
        locales.flatMap(({code, aliases = []}: Locale) => [code, ...aliases].map(alias => [alias, code])),
);

// ?lang= wins so a demo can be shown in a specific language regardless of the
// device, otherwise follow the browser. Only the part before the dash matters:
// fr-BE and fr-FR both map to fr.
export const resolveLanguage = (search: string, browserLanguage: string): string => {
    const requested = new URLSearchParams(search).get('lang');
    const candidates = [requested, browserLanguage].map(value => (value || '').split('-')[0].toLowerCase());
    return candidates.map(candidate => codeFor.get(candidate)).find(Boolean) || defaultLanguage;
};

i18next.use(initReactI18next).init({
    resources: Object.fromEntries(
            locales.map(locale => [locale.code, {translation: locale.translation}]),
    ),
    lng: resolveLanguage(window.location.search, navigator.language),
    fallbackLng: defaultLanguage,
    interpolation: {escapeValue: false},
});

// public/index.html ships a static lang="en". Keep the document in step with the
// resolved language, otherwise a French page still announces itself as English to
// screen readers and to browser translation.
document.documentElement.lang = i18next.language;
document.title = i18next.t('meta.title');

// Switching language keeps four things in step: i18next itself, `<html lang>` for
// assistive technology and browser translation, the document title that ends up in the
// tab and in a shared link's preview, and `?lang=` so a refresh or a shared link lands
// in the same language.
export const changeLanguage = async (language: string): Promise<void> => {
    await i18next.changeLanguage(language);
    document.documentElement.lang = language;
    document.title = i18next.t('meta.title');
    const url = new URL(window.location.href);
    url.searchParams.set('lang', language);
    window.history.replaceState({}, '', url);
};

export default i18next;
